import { PremiseOnSinglePremise } from 'app/premises/Premise';
import { useTranslation } from 'common/hooks/useTranslation';
import React, { FC, useEffect, useState, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';

import { G } from '@mobily/ts-belt';
import { Box, List, ListItem } from '@mui/material';

import { AvailableOutletTypes } from 'app/outlets/AvailiableOutletTypes';
import { useAzureOutletPhotoUpload } from '../../hooks/useAzureOutletPhotoUpload';
import { usePremiseOutlets } from '../../hooks/useOutlets';
import { Outlet } from '../../Outlet';
import { ImagePicker } from './components/ImagePicker';
import { TPOutlet } from '../../TPOutlet';
import { usePremiseTPOutlets } from '../../hooks/useTPOutlets';
import { OutletTypes } from '../OutletMenu/OutletMenu';
import { PhotoDialogs } from '../../../../common/components/PhotoDialogs/PhotoDialogs';
import { DeletePhotoButton } from '../../../../common/components/DeletePhotoButton/DeletePhotoButton';
import { removeLocalPhoto } from '../../../../common/offline/offlinePhotoStorage';
import { useLocalPhotos } from '../../../../common/offline/useLocalPhotos';
import { isBlobUrl } from '../../../../common/utils';

export const OutletPhotos: FC<{
  outlet: Outlet | TPOutlet;
  premise: PremiseOnSinglePremise;
  outletType: OutletTypes;
}> = ({ outlet, premise, outletType }) => {
  const { t } = useTranslation();
  const {
    updateOutletPhoto,
    isSuccessDeletePhoto: isSuccessDeleteOutletPhoto,
    isDeletingPhoto: isDeletingOutletPhoto,
    deletePhoto: deleteOutletPhoto,
  } = usePremiseOutlets(premise);
  const {
    updateTPOutletPhoto,
    isSuccessDeletePhoto: isSuccessDeleteTPOutletPhoto,
    isDeletingPhoto: isDeletingTPOutletPhoto,
    deletePhoto: deleteTPOutletPhoto,
  } = usePremiseTPOutlets(premise);

  const [isConfirmationWindowOpen, setIsConfirmationWindowOpen] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState<string | null>(null);
  const [enlargedPhotoUrl, setEnlargedPhotoUrl] = useState<null | string>(null);
  // Track blob URLs that were added optimistically during this session
  const [sessionBlobUrls, setSessionBlobUrls] = useState<string[]>([]);
  const prevRealUrlCountRef = useRef<number>(0);

  // Determine entity type for offline storage lookup
  const entityType = outletType === AvailableOutletTypes.OUTLET ? 'outlet' : 'tpOutlet';

  const isDeletingPhoto = isDeletingOutletPhoto || isDeletingTPOutletPhoto;
  const isSuccessDeletePhoto = isSuccessDeleteOutletPhoto || isSuccessDeleteTPOutletPhoto;
  const {
    mutate,
    data: uploadedPhotoUrl,
    isLoading: isLoadingPhotoUpload,
    isError: isErrorPhotoUpload,
    isSuccess,
    isOfflineQueued,
  } = useAzureOutletPhotoUpload();

  // Separate real URLs from blob URLs in the outlet's images
  const realUrls = useMemo(() => outlet.imagesUrls.filter((url) => !isBlobUrl(url)), [outlet.imagesUrls]);

  // Get persisted local photos from offlinePhotoStorage with automatic updates
  // This hook subscribes to photo changes and re-renders when photos are added/removed
  const persistedLocalPhotos = useLocalPhotos(entityType, outlet.id);

  // Combine session blob URLs with persisted ones, avoiding duplicates
  const allLocalPhotos = useMemo(() => {
    const persistedSet = new Set(persistedLocalPhotos);
    const sessionOnly = sessionBlobUrls.filter((url) => !persistedSet.has(url));
    return [...persistedLocalPhotos, ...sessionOnly];
  }, [persistedLocalPhotos, sessionBlobUrls]);

  // When real URLs increase (meaning server data was updated after sync),
  // clear the session blob URLs since they've been replaced by real URLs
  useEffect(() => {
    if (realUrls.length > prevRealUrlCountRef.current && allLocalPhotos.length > 0) {
      // New real URLs arrived - clear session blob URLs
      setSessionBlobUrls([]);
    }
    prevRealUrlCountRef.current = realUrls.length;
  }, [realUrls.length, allLocalPhotos.length]);

  // Combined URLs for display: real URLs from server + locally tracked blob URLs
  const displayUrls = useMemo(() => [...realUrls, ...allLocalPhotos], [realUrls, allLocalPhotos]);

  useEffect(() => {
    if (isSuccess && G.isNotNullable(uploadedPhotoUrl)) {
      if (isOfflineQueued) {
        // Photo was queued for offline upload
        // Track the blob URL locally for display (instead of in React Query cache)
        // The fileSyncManager will send the real mutation after file upload
        toast.success(t('general.photoQueuedForUpload'));
        setSessionBlobUrls((prev) => [...prev, uploadedPhotoUrl]);
      } else {
        // Online upload completed - send mutation with real Azure URL
        toast.success(t('general.updatedSuccessfully'));
        if (outletType === AvailableOutletTypes.OUTLET) {
          updateOutletPhoto(outlet.id, uploadedPhotoUrl);
        } else if (outletType === AvailableOutletTypes.TPOUTLET) {
          updateTPOutletPhoto(outlet.id, uploadedPhotoUrl);
        }
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isErrorPhotoUpload) {
      toast.error(t('error.generalMessage'));
    }
  }, [isErrorPhotoUpload]);

  useEffect(() => {
    if (isSuccessDeletePhoto) {
      setEnlargedPhotoUrl(null);
      setIsConfirmationWindowOpen(false);

      toast.success(t('general.deletedSuccessfully'));
    }
  }, [isSuccessDeletePhoto]);

  const handleInitDelete = (e: React.MouseEvent<HTMLButtonElement>, photoUrl: string) => {
    e.stopPropagation();
    setUrlToDelete(photoUrl);
    setIsConfirmationWindowOpen(true);
  };
  const handleDelete = async () => {
    const url = enlargedPhotoUrl || urlToDelete;
    if (!url) return;

    // If it's a blob URL, remove from local storage (no server call needed)
    if (isBlobUrl(url)) {
      setSessionBlobUrls((prev) => prev.filter((u) => u !== url));
      removeLocalPhoto(entityType, outlet.id, url);
      setEnlargedPhotoUrl(null);
      setIsConfirmationWindowOpen(false);
      toast.success(t('general.deletedSuccessfully'));
      return;
    }

    // For real URLs, call the server delete
    if (outletType === AvailableOutletTypes.OUTLET) {
      await deleteOutletPhoto(url, outlet?.id ?? null);
    } else if (outletType === AvailableOutletTypes.TPOUTLET) {
      await deleteTPOutletPhoto(url, outlet?.id ?? null);
    }
  };

  return (
    <>
      <PhotoDialogs
        isConfirmationWindowOpen={isConfirmationWindowOpen}
        isDeletingPhoto={isDeletingPhoto}
        setIsConfirmationWindowOpen={setIsConfirmationWindowOpen}
        onDelete={handleDelete}
        enlargedPhotoUrl={enlargedPhotoUrl}
        onCloseEnlargedPhotoDialog={() => setEnlargedPhotoUrl(null)}
      />
      <List sx={{ display: 'flex', gap: 2, p: 0, flexWrap: 'wrap' }}>
        {displayUrls.map((photoUrl, index) => (
          <ListItem
            key={`incoming-fiber-${photoUrl}}`}
            sx={{ p: 0, flex: '0 0', cursor: 'pointer' }}
            onClick={() => {
              setEnlargedPhotoUrl(photoUrl);
            }}
          >
            <Box width={96} height={96} sx={{ position: 'relative' }}>
              <Box component="img" src={photoUrl} alt={`outlet-image-${index}`} width="100%" height="100%" />
              <DeletePhotoButton handleInitDelete={(e) => handleInitDelete(e, photoUrl)} />
            </Box>
          </ListItem>
        ))}
        <ListItem sx={{ p: 0, flex: '0 0' }}>
          <ImagePicker
            isLoading={isLoadingPhotoUpload}
            onChange={(file) => {
              mutate({
                file,
                outletId: outlet.id,
                propertyId: premise.propertyId,
                uploadType: outletType === AvailableOutletTypes.TPOUTLET ? 'tpOutlet' : 'outlet',
              });
            }}
          />
        </ListItem>
      </List>
    </>
  );
};
